import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  routerNavigatedAction,
  SerializedRouterStateSnapshot,
} from '@ngrx/router-store';
import { PageConfig, PAGES_CONFIG } from '../page.config';
import { map, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { PageActions, pageFeature } from './page.feature';
import { PageService } from '../service/page.service';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';

@Injectable()
export class PageEffects {
  private router = inject(Router);
  private store = inject(Store);
  private action$ = inject(Actions);
  private pageService = inject(PageService);

  // Persistence in terms of URLs
  updatePageUrl$ = createEffect(() =>
    this.action$.pipe(
      ofType(routerNavigatedAction),
      switchMap(action => {
        const routerState = action.payload.routerState;
        const pageConfig = this.pageConfigForRouteState(routerState);
        return pageConfig ? of({ routerState, pageConfig }) : of();
      }),
      map(({ routerState, pageConfig }) =>
        PageActions.pageVisited({
          page: pageConfig.pageName,
          url: routerState.url,
        }),
      ),
    ),
  );

  updateActiveMenu$ = createEffect(() =>
    this.action$.pipe(
      ofType(routerNavigatedAction),
      switchMap(action => {
        const routerState = action.payload.routerState;
        const pageConfig = this.pageConfigForRouteState(routerState);
        return pageConfig ? of({ routerState, pageConfig }) : of();
      }),
      map(({ pageConfig }) =>
        PageActions.setActiveMenu({ activeMenu: pageConfig.menuItem }),
      ),
    ),
  );

  goToPage$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(PageActions.goToPage),
        withLatestFrom(this.store.select(pageFeature.selectPages)),
        switchMap(([action, pages]) => {
          const pageName = action.page;
          const pageConfig = this.pageConfigForPage(pageName);
          return pageConfig ? of({ pages, pageConfig }) : of();
        }),
        tap(({ pageConfig, pages }) =>
          this.router.navigateByUrl(
            pages.get(pageConfig.pageName) ?? pageConfig.defaultUrl,
          ),
        ),
      ),
    { dispatch: false },
  );

  private pageConfigForRouteState(
    routeState: SerializedRouterStateSnapshot,
  ): PageConfig | undefined {
    return PAGES_CONFIG.find(({ routeTest }) => routeTest(routeState));
  }

  private pageConfigForPage(pageName: string): PageConfig | undefined {
    return PAGES_CONFIG.find(config => config.pageName === pageName);
  }
}
